import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Snackbar,
  InputAdornment,
  Pagination,
} from '@mui/material'
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import api from '../api/client'
import { usePortalStore } from '../stores/portalStore'

interface TicketSummary {
  id: number
  number: string | null
  title: string
  group_id: number
  state_id: number
  priority_id: number
  customer_id: number | null
  owner_id: number | null
  organization_id: number | null
  article_count: number
  created_at: string
  updated_at: string
  close_at: string | null
}

interface TicketsResponse {
  tickets: TicketSummary[]
  total_count: number
  page: number
  per_page: number
}

interface AttachmentSummary {
  id: number
  filename: string
  size: string
  mime_type: string
}

interface Article {
  id: number
  ticket_id: number
  type: string
  body: string
  content_type: string | null
  internal: boolean
  created_at: string
  from: string | null
  to: string | null
  subject: string | null
  attachments: AttachmentSummary[]
}

interface TicketDetail {
  ticket: Record<string, any>
  articles: Article[]
}

interface ZammadMe {
  id: number
  email: string
  firstname: string
  lastname: string
  organization_id: number | null
  role_ids: number[]
  is_admin_or_agent: boolean
}

interface StateItem { id: number; name: string; default_create: boolean; active: boolean }
interface PriorityItem { id: number; name: string; active: boolean }
interface GroupItem { id: number; name: string; active: boolean }
interface OrgItem { id: number; name: string }

type Scope = 'mine' | 'organization'
type StatusFilter = 'open' | 'all' | 'closed'

export default function TicketsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { config } = usePortalStore()
  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'
  const glassSx = glassmorphism
    ? { bgcolor: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }
    : {}

  const [selectedTicket, setSelectedTicket] = useState<number | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [scope, setScope] = useState<Scope>('mine')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open')
  const [page, setPage] = useState(1)
  const [orgId, setOrgId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [snack, setSnack] = useState('')
  const [replyFiles, setReplyFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Zammad user profile
  const { data: me } = useQuery<ZammadMe>({
    queryKey: ['ticket-me'],
    queryFn: async () => (await api.get('/tickets/me')).data,
  })

  // Metadata
  const { data: states = [] } = useQuery<StateItem[]>({
    queryKey: ['ticket-states'],
    queryFn: async () => (await api.get('/tickets/states')).data,
  })
  const { data: priorities = [] } = useQuery<PriorityItem[]>({
    queryKey: ['ticket-priorities'],
    queryFn: async () => (await api.get('/tickets/priorities')).data,
  })
  const { data: groups = [] } = useQuery<GroupItem[]>({
    queryKey: ['ticket-groups'],
    queryFn: async () => (await api.get('/tickets/groups')).data,
  })

  // Organizations (only for admin/agent)
  const { data: organizations = [] } = useQuery<OrgItem[]>({
    queryKey: ['ticket-organizations'],
    queryFn: async () => (await api.get('/tickets/organizations')).data,
    enabled: !!me?.is_admin_or_agent,
  })

  // Tickets list
  const { data: ticketsResponse, isLoading, error, refetch } = useQuery<TicketsResponse>({
    queryKey: ['tickets', scope, statusFilter, page, orgId],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: '25',
        status: statusFilter,
        scope: scope,
      }
      if (orgId !== null) params.organization_id = String(orgId)
      const res = await api.get('/tickets/tickets', { params })
      return res.data
    },
  })

  const tickets = ticketsResponse?.tickets ?? []
  const totalCount = ticketsResponse?.total_count ?? 0
  const totalPages = Math.ceil(totalCount / 25)

  // Filter by search locally
  const filteredTickets = search.trim()
    ? tickets.filter((tkt) => {
        const q = search.toLowerCase()
        return (tkt.title || '').toLowerCase().includes(q)
          || (tkt.number || '').toLowerCase().includes(q)
      })
    : tickets

  // Ticket detail
  const { data: detail, refetch: refetchDetail } = useQuery<TicketDetail>({
    queryKey: ['ticket', selectedTicket],
    queryFn: async () => (await api.get(`/tickets/tickets/${selectedTicket}`)).data,
    enabled: !!selectedTicket,
  })

  // Create ticket
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [newPriority, setNewPriority] = useState(2)
  const [newGroup, setNewGroup] = useState(1)

  const createMutation = useMutation({
    mutationFn: async () => api.post('/tickets/tickets', {
      title: newTitle, group_id: newGroup, priority_id: newPriority, body: newBody,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      setCreateOpen(false)
      setNewTitle('')
      setNewBody('')
      setSnack(t('tickets.createSuccess'))
    },
  })

  // Reply
  const [replyBody, setReplyBody] = useState('')
  const replyMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = { body: replyBody }
      if (replyFiles.length > 0) {
        payload.attachments = await Promise.all(
          replyFiles.map(async (file) => {
            const data = await new Promise<string>((resolve) => {
              const reader = new FileReader()
              reader.onload = () => resolve((reader.result as string).split(',')[1])
              reader.readAsDataURL(file)
            })
            return { filename: file.name, data, 'mime-type': file.type || 'application/octet-stream' }
          })
        )
      }
      return api.post(`/tickets/tickets/${selectedTicket}/articles`, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', selectedTicket] })
      setReplyBody('')
      setReplyFiles([])
    },
  })

  const downloadAttachment = (ticketId: number, articleId: number, attachmentId: number, filename: string) => {
    const url = `/tickets/attachments/${ticketId}/${articleId}/${attachmentId}`
    api.get(url, { responseType: 'blob' }).then((res) => {
      const blob = new Blob([res.data])
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      link.remove()
      window.URL.revokeObjectURL(link.href)
    })
  }

  const formatSize = (bytes: string | number) => {
    const b = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes
    if (isNaN(b) || b === 0) return ''
    if (b < 1024) return `${b} B`
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
    return `${(b / (1024 * 1024)).toFixed(1)} MB`
  }

  // Change state
  const changeStateMutation = useMutation({
    mutationFn: async (stateId: number) => api.put(`/tickets/tickets/${selectedTicket}/state`, { state_id: stateId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket', selectedTicket] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || t('common.error')
      setSnack(msg)
    },
  })

  const stateName = (id: number) => {
    const s = states.find((s) => s.id === id)
    return s ? s.name : String(id)
  }
  const stateLabel = (name: string) => {
    const map: Record<string, string> = {
      new: t('tickets.new'), open: t('tickets.open'), closed: t('tickets.closed'),
      'pending reminder': t('tickets.pendingReminder'), 'pending close': t('tickets.pendingClose'),
      'esperando respuesta': t('tickets.waitingForResponse'), merged: t('tickets.closed'),
    }
    return map[name] || name
  }
  const priorityLabel = (id: number) => {
    if (id === 1) return t('tickets.low')
    if (id === 3) return t('tickets.high')
    return t('tickets.normal')
  }
  const stateColor = (name: string): 'default' | 'info' | 'warning' | 'success' | 'error' => {
    if (name === 'closed' || name === 'merged') return 'default'
    if (name === 'new') return 'info'
    if (name.startsWith('pending') || name === 'esperando respuesta') return 'warning'
    return 'success'
  }
  const priorityColor = (id: number): 'default' | 'error' | 'primary' => {
    if (id === 3) return 'error'
    if (id === 1) return 'default'
    return 'primary'
  }
  const formatDate = (d: string) => {
    if (!d) return ''
    return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  const errorMsg = error
    ? String(error)?.includes('401') ? t('tickets.authFailed')
    : String(error)?.includes('403') ? t('common.accessDenied')
    : t('common.error')
  : ''

  // Detail view
  if (selectedTicket && detail) {
    const ticket = detail.ticket
    const closeState = states.find((s) => s.name === 'closed')
    const openState = states.find((s) => s.name === 'open' || s.name === 'new')
    const isClosed = ticket.state_id === closeState?.id

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => setSelectedTicket(null)} size="small"><BackIcon /></IconButton>
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            #{ticket.number || ticket.id} — {ticket.title}
          </Typography>
          <IconButton size="small" onClick={() => { queryClient.invalidateQueries({ queryKey: ['ticket', selectedTicket] }); refetchDetail(); }} title={t('tickets.refresh')} disabled={detail === undefined}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          {isClosed && openState ? (
            <Button size="small" variant="contained" color="primary" onClick={() => changeStateMutation.mutate(openState.id)} disabled={changeStateMutation.isPending}>
              {changeStateMutation.isPending ? '...' : t('tickets.reopenTicket')}
            </Button>
          ) : closeState ? (
            <Button size="small" variant="contained" color="error" onClick={() => changeStateMutation.mutate(closeState.id)} disabled={changeStateMutation.isPending}>
              {changeStateMutation.isPending ? '...' : t('tickets.closeTicket')}
            </Button>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={stateLabel(stateName(ticket.state_id))} color={stateColor(stateName(ticket.state_id))} size="small" />
          <Chip label={priorityLabel(ticket.priority_id)} color={priorityColor(ticket.priority_id)} size="small" />
          <Typography variant="caption" color="text.secondary">{formatDate(ticket.created_at)}</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          {(detail.articles || []).map((a) => {
            const isHtml = a.content_type === 'text/html' || (a.body && a.body.includes('<'))
            return (
              <Card key={a.id} variant="outlined" sx={{ mb: 1, ...glassSx }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>{a.from || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(a.created_at)}</Typography>
                  </Box>
                  {isHtml ? (
                    <Box
                      sx={{
                        '& p': { margin: '0.4em 0' },
                        fontSize: '0.875rem',
                        opacity: a.internal ? 0.7 : 1,
                        fontStyle: a.internal ? 'italic' : 'normal',
                      }}
                      dangerouslySetInnerHTML={{ __html: a.body }}
                    />
                  ) : (
                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.875rem', opacity: a.internal ? 0.7 : 1, fontStyle: a.internal ? 'italic' : 'normal' }}>
                      {a.body}
                    </Box>
                  )}
                  {a.attachments && a.attachments.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {a.attachments.map((att) => (
                        <Chip
                          key={att.id}
                          icon={<DownloadIcon fontSize="small" />}
                          label={`${att.filename}${formatSize(att.size) ? ` (${formatSize(att.size)})` : ''}`}
                          size="small"
                          variant="outlined"
                          onClick={() => downloadAttachment(a.ticket_id, a.id, att.id, att.filename)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </Box>

        {!isClosed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder={t('tickets.replyPlaceholder')}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: glassmorphism ? 'rgba(255,255,255,0.75)' : 'background.paper',
                  },
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  disabled={!replyBody.trim() || replyMutation.isPending}
                  onClick={() => replyMutation.mutate()}
                >
                  {t('tickets.send')}
                </Button>
                <Button
                  variant={glassmorphism ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={glassmorphism ? { bgcolor: 'rgba(255,255,255,0.75)', color: 'text.primary', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } } : {}}
                >
                  {t('tickets.attach')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    setReplyFiles((prev) => [...prev, ...files])
                    e.target.value = ''
                  }}
                />
              </Box>
            </Box>
            {replyFiles.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {replyFiles.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.name}
                    size="small"
                    onDelete={() => setReplyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    )
  }

  // List view
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" component="h2">{t('tickets.title')}</Typography>
          <IconButton size="small" onClick={() => { queryClient.invalidateQueries({ queryKey: ['tickets'] }); refetch(); }} title={t('tickets.refresh')} disabled={isLoading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} size="small" sx={glassSx}>
          {t('tickets.createTicket')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(_, v) => { if (v) { setScope(v); setPage(1) } }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                bgcolor: glassmorphism ? 'rgba(255,255,255,0.65)' : 'action.hover',
                backdropFilter: glassmorphism ? 'blur(10px)' : undefined,
                WebkitBackdropFilter: glassmorphism ? 'blur(10px)' : undefined,
              },
              '& .Mui-selected': {
                bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : 'primary.main',
                color: glassmorphism ? 'text.primary' : 'primary.contrastText',
              },
            }}
          >
            <ToggleButton value="mine"><PersonIcon sx={{ mr: 0.5, fontSize: 18 }} />{t('tickets.myTickets')}</ToggleButton>
            <ToggleButton value="organization"><GroupsIcon sx={{ mr: 0.5, fontSize: 18 }} />{t('tickets.orgTickets')}</ToggleButton>
          </ToggleButtonGroup>

          {me?.is_admin_or_agent && organizations.length > 0 && scope === 'organization' && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('tickets.allOrgs')}</InputLabel>
              <Select
                value={orgId ?? ''}
                onChange={(e) => { setOrgId(e.target.value === '' ? null : Number(e.target.value)); setPage(1) }}
                label={t('tickets.allOrgs')}
              >
                <MenuItem value="">{t('tickets.allOrgs')}</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(1) } }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              bgcolor: glassmorphism ? 'rgba(255,255,255,0.65)' : 'action.hover',
              backdropFilter: glassmorphism ? 'blur(10px)' : undefined,
              WebkitBackdropFilter: glassmorphism ? 'blur(10px)' : undefined,
            },
            '& .Mui-selected': {
              bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : 'primary.main',
              color: glassmorphism ? 'text.primary' : 'primary.contrastText',
            },
          }}
        >
          <ToggleButton value="open">{t('tickets.filterOpen')}</ToggleButton>
          <ToggleButton value="all">{t('tickets.filterAll')}</ToggleButton>
          <ToggleButton value="closed">{t('tickets.filterClosed')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder={t('tickets.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            bgcolor: glassmorphism ? 'rgba(255,255,255,0.75)' : 'background.paper',
          },
        }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : errorMsg ? (
        <Alert severity={errorMsg.includes('401') ? 'warning' : 'error'}>{errorMsg}</Alert>
      ) : filteredTickets.length === 0 ? (
        <Alert severity="info">{t('tickets.noTickets')}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredTickets.map((tkt) => (
            <Card
              key={tkt.id}
              variant="outlined"
              sx={{ cursor: 'pointer', ...glassSx, '&:hover': { bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : 'action.hover' } }}
              onClick={() => setSelectedTicket(tkt.id)}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1, mr: 1 }} noWrap>
                    #{tkt.number || tkt.id} — {tkt.title}
                  </Typography>
                  <Chip label={stateLabel(stateName(tkt.state_id))} color={stateColor(stateName(tkt.state_id))} size="small" />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={priorityLabel(tkt.priority_id)} size="small" color={priorityColor(tkt.priority_id)} />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(tkt.updated_at || tkt.created_at)}
                  </Typography>
                  {tkt.article_count > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      {tkt.article_count} {t('tickets.replies').toLowerCase()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
          />
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tickets.createTicket')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('tickets.subject')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            size="small"
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('tickets.priority')}</InputLabel>
            <Select value={newPriority} onChange={(e) => setNewPriority(Number(e.target.value))} label={t('tickets.priority')}>
              {priorities.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {groups.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t('tickets.group')}</InputLabel>
              <Select value={newGroup} onChange={(e) => setNewGroup(Number(e.target.value))} label={t('tickets.group')}>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            multiline
            minRows={4}
            label={t('tickets.description')}
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!newTitle.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('tickets.createTicket')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  )
}